'use client';

import Button from '@/components/ui/Button';
import ErrorText from '@/components/ui/ErrorText';
import Input from '@/components/ui/Input';
import Link from '@/components/ui/Link';
import { useFestivalContext } from '@/contexts/FestivalContext';
import { isEarlyBirdOnly, ticketingAuthHref } from '@/utils/early-bird';
import { getGiftRedemptionQuery } from '@/utils/gift-redemption';
import { useSearchParams } from 'next/navigation';
import { useDialogContext } from '@/contexts/DialogContext';
import { PathEnum } from '@/i18n/locale-config';
import { buildLocalizedHref, useRouter } from '@/i18n/navigation';
import { useUserStore } from '@/store/useUserStore';
import { cn } from '@/utils/tailwind-merge';
import { loginSchema } from '@/utils/validation-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

type FormData = z.infer<typeof loginSchema>;

export default function SignInForm({
    className,
    openedInDialog = false,
}: {
    className?: string;
    openedInDialog?: boolean;
}) {
    const t = useTranslations();
    const locale = useLocale();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { user, login, session, isInitialized } = useUserStore();
    const router = useRouter();
    const { festival, basePath } = useFestivalContext();
    const earlyBirdOnly = isEarlyBirdOnly(festival);
    const searchParams = useSearchParams();
    const giftQuery = getGiftRedemptionQuery(searchParams);
    const returnToCheckout =
        (earlyBirdOnly || !!giftQuery) && searchParams.get('returnTo') === 'checkout';
    const destination = returnToCheckout
        ? `${PathEnum.Checkout}${giftQuery}`
        : `${PathEnum.Account}${giftQuery}`;
    const canRedirect = !!user && (!(earlyBirdOnly || giftQuery) || (!!session && isInitialized));
    const loggingIn = useRef(false);
    useEffect(() => {
        // A fresh login navigates from onSubmit instead
        if (canRedirect && !openedInDialog && !loggingIn.current) router.replace(destination);
    }, [canRedirect, openedInDialog, router, destination]);

    const { dequeueDialog } = useDialogContext();

    const {
        handleSubmit,
        register,
        reset,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(loginSchema),
    });

    const onSubmit = async (formData: FormData) => {
        setSubmitting(true);
        setErrorMessage(null);

        // Check if user exists before attempting login
        try {
            const response = await fetch(`${basePath}/api/check-user-exists/${giftQuery}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: formData.email }),
            });
            const { exists } = await response.json();

            if (!exists) {
                setErrorMessage(t('Form.emailNotRegistered'));
                setSubmitting(false);
                return;
            }
        } catch (error) {
            // If check fails, proceed with login anyway
            console.error('Error checking user existence:', error);
        }

        loggingIn.current = true;
        const result = await login(formData.email, formData.password);
        if (result.success) {
            reset();

            // The client router cache may still hold the logged-out payload of the
            // destination (a redirect to sign-in), which would loop back here and
            // leave the page blank. A full navigation renders it with the new session.
            if (!openedInDialog) {
                window.location.assign(buildLocalizedHref(destination, basePath, locale));
                return;
            }
            router.refresh();
        } else {
            loggingIn.current = false;
            const translationKeys = ['emailNotVerified', 'emailNotRegistered'];
            if (translationKeys.includes(result.message)) {
                setErrorMessage(t(`Form.${result.message}`));
            } else {
                setErrorMessage(result.message);
            }
        }

        if (openedInDialog) {
            dequeueDialog();
        }

        setSubmitting(false);
    };

    const getFieldError = (fieldName: keyof FormData) =>
        errors[fieldName]?.message && errors[fieldName]?.message !== 'Required'
            ? t(`FieldErrors.${errors[fieldName]?.message}`)
            : '';

    if (canRedirect && !openedInDialog) {
        return null;
    }

    return (
        <form
            className={cn(
                'flex flex-col gap-7 justify-center items-center relative text-white',
                className,
            )}
            noValidate
            onSubmit={handleSubmit(onSubmit)}
        >
            <h4 className="text-h4 lg:text-h1 uppercase text-white">{t('Form.signIn')}</h4>
            <div className="grid grid-cols-1 gap-3 max-w-[460px] w-full">
                <Input
                    {...register('email')}
                    className="newsletter-input"
                    type="email"
                    name="email"
                    id="email"
                    placeholder={t('Form.email')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('email')}
                    required
                />
                <Input
                    {...register('password')}
                    className="newsletter-input"
                    type="password"
                    name="password"
                    id="password"
                    placeholder={t('Form.password')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('password')}
                    required
                />
                {errorMessage && (
                    <div className="font-trade-ghotic font-light flex flex-col">
                        <ErrorText error={errorMessage} />
                    </div>
                )}
                <Button
                    className="w-full"
                    textClasses="text-white"
                    type="submit"
                    variant="secondary"
                    title={t('Form.signIn')}
                    submitting={submitting}
                    disabled={submitting}
                />
                {!openedInDialog && (
                    <>
                        <p className="mt-7 text-xl font-helvetica-neue font-light leading-6 text-center">
                            <Link
                                href={ticketingAuthHref(
                                    PathEnum.ForgotPassword,
                                    returnToCheckout,
                                    giftQuery,
                                )}
                                className="text-primary"
                                title={t('Form.forgotPassword')}
                            />
                        </p>
                        <p className="mt-2 text-xl font-helvetica-neue font-light leading-6 text-center">
                            {t('Common.dontHaveAccount')}{' '}
                            <Link
                                href={ticketingAuthHref(
                                    PathEnum.SignUp,
                                    returnToCheckout,
                                    giftQuery,
                                )}
                                className="text-primary"
                                title={t('Common.joinNow')}
                            />
                        </p>
                    </>
                )}
            </div>
        </form>
    );
}
